import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/admin-auth-context";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const formatVal = (v, type) => {
  if (v == null || v === "") return "—";
  if (type === "currency") return `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
  if (type === "number") return Number(v).toLocaleString("es-MX");
  if (type === "date") {
    try {
      return new Date(v).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(v);
    }
  }
  if (type === "bool") return v ? "Sí" : "No";
  return String(v);
};

const StatusPill = ({ value, statusMap }) => {
  const cfg = statusMap?.[value] || { label: value, color: "slate" };
  const styles = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold border ${
        styles[cfg.color] || styles.slate
      }`}
      style={{ borderRadius: 4 }}
    >
      {cfg.label}
    </span>
  );
};

/**
 * Generic table-based CRUD page.
 * config: { title, resource, columns: [{key, label, type?, statusMap?}], fields: [{key, label, type, required, options?}] }
 */
export default function CrudPage({ config, canMutate = true, transformOnCreate, transformBeforeSubmit }) {
  const { api } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {} new, item edit

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${config.resource}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.resource]);

  const onSave = async (data) => {
    try {
      const payload = transformBeforeSubmit ? transformBeforeSubmit(data) : data;
      if (data.id) {
        await api.put(`/${config.resource}/${data.id}`, payload);
        toast.success("Actualizado");
      } else {
        const initialPayload = transformOnCreate ? transformOnCreate(payload) : payload;
        await api.post(`/${config.resource}`, initialPayload);
        toast.success("Creado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/${config.resource}/${item.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid={`admin-${config.resource}-page`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            {config.eyebrow || "Operaciones"}
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
            {config.title}
          </h1>
        </div>
        {canMutate && (
          <button
            onClick={() => setEditing({})}
            data-testid={`admin-${config.resource}-new`}
            className="btn-primary"
          >
            <Plus size={16} weight="bold" /> Nuevo
          </button>
        )}
      </div>

      <div
        className="bg-white border border-slate-200 overflow-x-auto"
        style={{ borderRadius: 4 }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {config.columns.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500"
                >
                  {c.label}
                </th>
              ))}
              {canMutate && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={config.columns.length + 1} className="px-4 py-8 text-center text-slate-400">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={config.columns.length + 1} className="px-4 py-8 text-center text-slate-400">
                  Sin registros
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`admin-${config.resource}-row-${it.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  {config.columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-slate-700">
                      {c.type === "status" ? (
                        <StatusPill value={it[c.key]} statusMap={c.statusMap} />
                      ) : c.render ? (
                        c.render(it)
                      ) : (
                        formatVal(it[c.key], c.type)
                      )}
                    </td>
                  ))}
                  {canMutate && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditing(it)}
                        className="p-1.5 text-slate-500 hover:text-[var(--icor-blue)]"
                        aria-label="Editar"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(it)}
                        className="p-1.5 text-slate-500 hover:text-red-600 ml-2"
                        aria-label="Eliminar"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <Dialog
          fields={config.fields}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          title={editing.id ? "Editar registro" : "Nuevo registro"}
        />
      )}
    </div>
  );
}

const Dialog = ({ fields, initial, onClose, onSave, title }) => {
  const [data, setData] = useState({ ...initial });

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="admin-crud-dialog">
      <div
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ borderRadius: 4 }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="font-display text-xl font-semibold tracking-tight text-[var(--icor-navy)]">
            {title}
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-slate-500 hover:text-slate-900">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                {f.label}
                {f.required && <span className="text-[var(--icor-blue)] ml-1">*</span>}
              </label>
              {f.type === "select" ? (
                <select
                  required={f.required}
                  className="icor-input"
                  value={data[f.key] ?? ""}
                  onChange={(e) => update(f.key, e.target.value)}
                  data-testid={`admin-field-${f.key}`}
                >
                  <option value="">Selecciona…</option>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea
                  required={f.required}
                  rows={3}
                  className="icor-input resize-none"
                  value={data[f.key] ?? ""}
                  onChange={(e) => update(f.key, e.target.value)}
                  data-testid={`admin-field-${f.key}`}
                />
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!data[f.key]}
                    onChange={(e) => update(f.key, e.target.checked)}
                    data-testid={`admin-field-${f.key}`}
                  />
                  {f.checkboxLabel || "Sí"}
                </label>
              ) : (
                <input
                  type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"}
                  step={f.type === "number" ? "any" : undefined}
                  required={f.required}
                  className="icor-input"
                  value={data[f.key] ?? ""}
                  onChange={(e) =>
                    update(
                      f.key,
                      f.type === "number" && e.target.value !== ""
                        ? Number(e.target.value)
                        : e.target.value
                    )
                  }
                  placeholder={f.placeholder}
                  data-testid={`admin-field-${f.key}`}
                />
              )}
            </div>
          ))}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline-dark"
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" data-testid="admin-crud-save">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
